import { AlertCircle, CheckCircle, Info, X } from "lucide-react";
import React from "react";

interface ConfirmDialogProps {
  cancelText?: string;
  confirmText?: string;
  icon?: React.ReactNode;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
  title?: string;
  type?: DialogType;
}

type DialogType = "confirm" | "error" | "info" | "success" | "warning";

const getDefaultIcon = (type: DialogType) => {
  const iconClass = "h-8 w-8";
  switch (type) {
    case "error":
      return <AlertCircle className={`${iconClass} text-error`} />;
    case "info":
      return <Info className={`${iconClass} text-info`} />;
    case "success":
      return <CheckCircle className={`${iconClass} text-success`} />;
    case "warning":
      return <AlertCircle className={`${iconClass} text-warning`} />;
    default:
      return <AlertCircle className={`${iconClass} text-primary`} />;
  }
};

const getButtonColor = (type: DialogType) => {
  switch (type) {
    case "error":
      return "btn-error";
    case "info":
      return "btn-info";
    case "success":
      return "btn-success";
    case "warning":
      return "btn-warning";
    default:
      return "btn-primary";
  }
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  cancelText = "Cancel",
  confirmText = "Yes",
  icon,
  message,
  onCancel,
  onConfirm,
  open,
  title = "Confirm",
  type = "confirm",
}) => {
  if (!open) return null;
  return (
    <div className="animate-fadeIn fixed inset-0 z-50 flex items-center justify-center bg-base-content/50 backdrop-blur-sm transition-opacity duration-300">
      <div className="animate-slideUp mx-2 w-full max-w-md overflow-hidden rounded-2xl bg-base-100 p-6 shadow-2xl sm:mx-0">
        {/* Close button */}
        <button
          aria-label="Close"
          className="absolute right-4 top-4 rounded-lg p-1 text-base-content/60 transition hover:bg-base-200 hover:text-base-content focus:outline-none focus:ring-2 focus:ring-primary"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Icon and Title */}
        <div className="mb-4 flex items-start gap-4">
          <div className="flex-shrink-0">
            {icon || getDefaultIcon(type)}
          </div>
          <div className="flex-1 pt-1">
            <h2 className="text-lg font-bold text-base-content">
              {title}
            </h2>
          </div>
        </div>

        {/* Message */}
        <p className="mb-6 break-words text-sm text-base-content/70 leading-relaxed">
          {message}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            className="btn btn-outline btn-sm sm:btn-md"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            className={`btn btn-sm sm:btn-md text-base-100 ${getButtonColor(type)}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease;
        }
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slideUp {
          animation: slideUp 0.25s cubic-bezier(.4,2,.3,1);
        }
      `}</style>
    </div>
  );
};

export default ConfirmDialog;
